import { auth } from '../config/firebase';
import { emailCacheService } from './emailCacheService';

const GMAIL_API_BASE =
  import.meta.env.VITE_GMAIL_API_BASE ||
  'https://us-central1-aideline-pro.cloudfunctions.net/api/gmail';

interface GmailMessageHeader {
  name: string;
  value: string;
}

interface GmailMessagePart {
  mimeType?: string;
  body?: { data?: string };
  parts?: GmailMessagePart[];
}

interface GmailMessage {
  id: string;
  threadId: string;
  labelIds?: string[];
  payload?: {
    headers?: GmailMessageHeader[];
    body?: { data?: string };
    parts?: GmailMessagePart[];
    mimeType?: string;
  };
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Not authenticated');
  }
  const idToken = await currentUser.getIdToken();
  return {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
}

export const gmailService = {
  async isConnected(userId: string): Promise<boolean> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${GMAIL_API_BASE}/oauth/status?userId=${encodeURIComponent(userId)}`,
        { headers }
      );
      if (!response.ok) return false;
      const data = await response.json();
      return !!data.connected;
    } catch {
      return false;
    }
  },

  async connect(userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const returnUrl = `${window.location.origin}/#settings?tab=email`;
    const response = await fetch(`${GMAIL_API_BASE}/oauth/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, returnUrl }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Failed to start Gmail OAuth flow: ${message}`);
    }

    const data = await response.json();
    if (!data?.authUrl) {
      throw new Error('Gmail OAuth start did not return an auth URL');
    }
    window.location.assign(data.authUrl);
  },

  async disconnect(userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${GMAIL_API_BASE}/oauth/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Failed to disconnect Gmail: ${message}`);
    }
  },

  async getAccessToken(userId: string): Promise<string> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${GMAIL_API_BASE}/oauth/access-token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Gmail access token request failed: ${message}`);
    }

    const tokenData = await response.json();
    if (!tokenData?.accessToken) {
      throw new Error('Gmail access token missing in response');
    }
    return tokenData.accessToken as string;
  },

  async fetchEmails(userId: string, maxResults: number = 100): Promise<any[]> {
    try {
      const cachedEmails = await emailCacheService.getCachedEmails(userId);
      const lastSyncDate = await emailCacheService.getLastSyncDate(userId);

      console.log(`Found ${cachedEmails.length} cached emails. Last sync: ${lastSyncDate}`);

      const newEmails = await this.fetchNewEmailsFromGmail(userId, lastSyncDate, maxResults);

      if (newEmails.length > 0) {
        await emailCacheService.syncNewEmails(userId, newEmails);
        return [...newEmails, ...cachedEmails].slice(0, 100);
      }

      return cachedEmails;
    } catch (error) {
      console.error('Error fetching emails:', error);
      throw error;
    }
  },

  async fetchNewEmailsFromGmail(
    userId: string,
    afterDate: Date | null,
    maxResults: number = 50
  ): Promise<any[]> {
    try {
      const accessToken = await this.getAccessToken(userId);

      let queryDate: string;
      if (afterDate) {
        queryDate = afterDate.toISOString().split('T')[0].replace(/-/g, '/');
      } else {
        const sixtyDaysAgo = new Date();
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
        queryDate = sixtyDaysAgo.toISOString().split('T')[0].replace(/-/g, '/');
      }

      const listResponse = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=${maxResults}&q=in:anywhere after:${queryDate}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!listResponse.ok) {
        throw new Error('Failed to fetch emails');
      }

      const listData = await listResponse.json();
      const messages = (listData.messages || []) as Array<{ id: string }>;
      const limitedMessages = messages.slice(0, 50);
      const batchSize = 10;
      const emails: any[] = [];

      for (let i = 0; i < limitedMessages.length; i += batchSize) {
        const batch = limitedMessages.slice(i, i + batchSize);

        const batchEmails = await Promise.all(
          batch.map(async (msg) => {
            try {
              const detailResponse = await fetch(
                `https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}?format=full`,
                {
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                }
              );

              if (detailResponse.ok) {
                const detail = (await detailResponse.json()) as GmailMessage;
                return this.parseGmailMessage(detail);
              }
            } catch (error) {
              console.error('Error fetching message:', error);
            }
            return null;
          })
        );

        emails.push(...batchEmails.filter((email) => email !== null));

        if (i + batchSize < limitedMessages.length) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      console.log(`Fetched ${emails.length} emails from Gmail`);
      return emails;
    } catch (error) {
      console.error('Error fetching emails:', error);
      throw error;
    }
  },

  parseGmailMessage(message: GmailMessage): any {
    const headers = message.payload?.headers || [];
    const getHeader = (name: string) => headers.find((h) => h.name === name)?.value || '';

    const subject = getHeader('Subject');
    const from = getHeader('From');
    const to = getHeader('To');
    const date = getHeader('Date');

    const labelIds = message.labelIds || [];
    const isSent = labelIds.includes('SENT');

    let body = '';
    let htmlBody = '';
    let isHtml = false;

    const decodeBase64 = (data: string) => {
      try {
        return atob(data.replace(/-/g, '+').replace(/_/g, '/'));
      } catch (error) {
        console.error('Error decoding base64:', error);
        return '';
      }
    };

    const extractBody = (payload: GmailMessagePart): void => {
      if (payload.body?.data) {
        const decoded = decodeBase64(payload.body.data);
        if (payload.mimeType === 'text/html') {
          htmlBody = decoded;
          isHtml = true;
        } else if (payload.mimeType === 'text/plain') {
          body = decoded;
        }
      }

      if (payload.parts) {
        payload.parts.forEach((part) => {
          if (part.mimeType === 'text/html' && part.body?.data) {
            htmlBody = decodeBase64(part.body.data);
            isHtml = true;
          } else if (part.mimeType === 'text/plain' && part.body?.data) {
            body = decodeBase64(part.body.data);
          } else if (part.parts) {
            extractBody(part);
          }
        });
      }
    };

    if (message.payload) {
      extractBody(message.payload);
    }

    const contentPreview = isHtml
      ? htmlBody
          .replace(/<style[^>]*>.*?<\/style>/gi, '')
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      : body;

    return {
      id: message.id,
      subject: subject || '(No Subject)',
      from: from || 'Unknown',
      to: to || 'Unknown',
      date: date ? new Date(date) : new Date(),
      content: contentPreview.slice(0, 5000),
      htmlContent: htmlBody,
      isHtml,
      isSent,
      direction: isSent ? 'sent' : 'received',
      threadId: message.threadId,
    };
  },

  async importGmailContacts(userId: string): Promise<any[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${GMAIL_API_BASE}/contacts/import?userId=${encodeURIComponent(userId)}&maxResults=1000`,
      { headers }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to fetch Gmail contacts: ${text}`);
    }

    const data = await response.json();
    const contacts = Array.isArray(data?.contacts) ? data.contacts : [];
    console.log(`Found ${contacts.length} Gmail contacts`);
    return contacts;
  },

  async sendEmail(userId: string, to: string, subject: string, body: string): Promise<void> {
    try {
      const accessToken = await this.getAccessToken(userId);

      const email = [`To: ${to}`, `Subject: ${subject}`, '', body].join('\r\n');
      const encodedEmail = btoa(email)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          raw: encodedEmail,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  },
};

